import type { SelectedFilterValue } from '@/components/shared';

/** The canonical country name in the geo catalog — matched on `value`, not `label`. */
export const UNITED_STATES = 'United States';

export interface StateSyncResult {
  /** The state selection that should now be applied. */
  state: SelectedFilterValue[];
  /** States dropped by this change — empty when nothing was removed. */
  removed: SelectedFilterValue[];
}

/** True when "United States" is among the selected countries, however many others are. */
export function hasUnitedStates(countries: readonly SelectedFilterValue[]): boolean {
  return countries.some((country) => country.value === UNITED_STATES);
}

/**
 * NPJB-559 — reconcile the State selection against Country.
 *
 * State is only meaningful for the U.S., so it may hold values only while
 * "United States" is selected (AC3). When the U.S. goes, the states go with it
 * (AC5) — and the caller gets them back in `removed` so they can be remembered for
 * restoration if the U.S. returns (AC8/AC9).
 *
 * Pure, mirroring `syncIndustryChange`: no store access, so it is trivially testable
 * and safe to call from every write path that can change `country`. Deliberately
 * returns the *same* array reference when nothing changes, so callers can skip a
 * pointless state write.
 */
export function syncStateOnCountryChange(
  nextCountries: readonly SelectedFilterValue[],
  currentStates: readonly SelectedFilterValue[],
): StateSyncResult {
  if (hasUnitedStates(nextCountries)) {
    return { state: currentStates as SelectedFilterValue[], removed: [] };
  }
  if (currentStates.length === 0) {
    return { state: currentStates as SelectedFilterValue[], removed: [] };
  }
  return { state: [], removed: [...currentStates] };
}
